/**
 * E06-F05-T05 — presentation model for the manual AI-check screen (DOM-free).
 *
 * Every rule the screen needs that is NOT React lives here so it can be unit-tested
 * without a DOM: the per-company status chip vocabulary, which rows may be started,
 * the checkbox-selection algebra, the batch-volume validation (open-decision #5:
 * user-typed volume, pre-filled {@link AI_RUN_BATCH_DEFAULT}=10, backstop
 * {@link AI_RUN_BATCH_CAP}=50) and the confirmation copy that must name the count.
 *
 * Invariant this model encodes (no-automatic-AI, decision-log 2026-07-20 + the
 * 2026-08-03 manual-only ADR): NOTHING here starts a run. It only decides what a
 * click would be allowed to do; the paid call is made by the view's mutation after an
 * explicit button press (and, for a multi-company run, an explicit confirmation).
 */
import {
  AI_RUN_BATCH_CAP,
  AI_RUN_BATCH_DEFAULT,
  describeRunFailure,
  isActiveAiRunStatus,
  MANUAL_CHECK_CRITERIA,
  MANUAL_EVIDENCE_PREFIX,
  MANUAL_NO_NOTE,
  type AiRunStatus,
  type ManualCheckCriterion,
  type ManualCheckInput,
  type ManualCheckVerdict,
  type PipelineRow,
  type RunFailure,
} from '@eer/shared';
import { formatUsd } from './researchTriageModel.js';

export { AI_RUN_BATCH_CAP, AI_RUN_BATCH_DEFAULT };

// ---------------------------------------------------------------------------
// status chips (one per company — the "current stage of the check" indicator)
// ---------------------------------------------------------------------------

export interface AiStatusMeta {
  /** Human label shown in the chip. */
  readonly label: string;
  /** Tailwind classes for the chip pill. */
  readonly chipClass: string;
  /** Font-awesome icon class. */
  readonly icon: string;
  /** True while the row is mid-flight (chip spins, checkbox is disabled). */
  readonly active: boolean;
}

/**
 * Chip vocabulary per `aiRunStatus`, verbatim from the design contour
 * (docs/proposals/research-ai-chain-scrape-first.md §7): idle → "Not checked",
 * gathering → "Collecting site", extracting → "Extracting data", verifying →
 * "Verifying", scored → "Ready", failed → "Failed".
 */
export const AI_STATUS_META: Record<AiRunStatus, AiStatusMeta> = {
  idle: {
    label: 'Not checked',
    chipClass: 'bg-slate-100 text-slate-500 border-slate-200',
    icon: 'fa-minus',
    active: false,
  },
  gathering: {
    label: 'Collecting site',
    chipClass: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: 'fa-spinner fa-spin',
    active: true,
  },
  extracting: {
    label: 'Extracting data',
    chipClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    icon: 'fa-spinner fa-spin',
    active: true,
  },
  verifying: {
    label: 'Verifying',
    chipClass: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: 'fa-spinner fa-spin',
    active: true,
  },
  scored: {
    label: 'Ready',
    chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: 'fa-circle-check',
    active: false,
  },
  failed: {
    label: 'Failed',
    chipClass: 'bg-red-50 text-red-700 border-red-200',
    icon: 'fa-triangle-exclamation',
    active: false,
  },
};

/** Chip meta for a status, tolerant of an unknown/absent value (→ idle). */
export function aiStatusMeta(status: AiRunStatus | string | null | undefined): AiStatusMeta {
  const key = (status ?? 'idle') as AiRunStatus;
  return AI_STATUS_META[key] ?? AI_STATUS_META.idle;
}

// ---------------------------------------------------------------------------
// what a click may do to a row
// ---------------------------------------------------------------------------

/** A row is startable unless a run is already mid-flight (never double-charge). */
export function isRunnable(row: Pick<PipelineRow, 'aiStatus'>): boolean {
  return !isActiveAiRunStatus(row.aiStatus);
}

/** A failed row gets its own Retry button on the same company (acceptance #4). */
export function isRetryable(row: Pick<PipelineRow, 'aiStatus'>): boolean {
  return row.aiStatus === 'failed';
}

/**
 * E06-F05-T08 — what a FAILED row says to a human.
 *
 * The table used to print `aiError` verbatim, i.e. the scraper's own technical string
 * (`GET http://acme.com/ → HTTP 403`), which tells a recruiter nothing. The API now
 * sends the split shape (human `aiError` + machine `aiErrorCode` + raw `aiErrorDetail`)
 * and this helper is the single place the view asks "what do I show".
 *
 * It classifies from the RAW text when the API sent one, so message and detail can
 * never disagree, and falls back to `aiError` for the rolling-deploy window (an older
 * API still sends the raw string there; `describeRunFailure` is idempotent, so a newer
 * API's already-human sentence survives untouched).
 */
export function rowFailure(row: PipelineRow): RunFailure | null {
  if (row.aiStatus !== 'failed') return null;
  return describeRunFailure(row.aiErrorDetail ?? row.aiError);
}

/** Ids of the rows a checkbox may select right now (mid-flight rows are locked). */
export function selectableIds(rows: readonly PipelineRow[]): number[] {
  return rows.filter(isRunnable).map((r) => r.id);
}

/** True while ANY row is mid-flight → the list keeps polling for status updates. */
export function hasActiveRuns(rows: readonly PipelineRow[]): boolean {
  return rows.some((r) => isActiveAiRunStatus(r.aiStatus));
}

/** Toggle one id in a selection (pure — returns a new array, order preserved). */
export function toggleId(selected: readonly number[], id: number): number[] {
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
}

/**
 * "Select first N" — the volume field's quick-select: take the first `volume`
 * selectable rows in the order the table shows them (rank order from the server).
 */
export function takeFirst(rows: readonly PipelineRow[], volume: number): number[] {
  if (!Number.isFinite(volume) || volume <= 0) return [];
  return selectableIds(rows).slice(0, Math.floor(volume));
}

/** Drop ids that are no longer selectable (a row went mid-flight under the user). */
export function pruneSelection(
  selected: readonly number[],
  rows: readonly PipelineRow[],
): number[] {
  const allowed = new Set(selectableIds(rows));
  return selected.filter((id) => allowed.has(id));
}

// ---------------------------------------------------------------------------
// batch volume (open-decision #5) + confirmation copy
// ---------------------------------------------------------------------------

/** Parse the volume input; null for anything that is not a positive integer. */
export function parseVolume(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n > 0 ? n : null;
}

export interface BatchValidation {
  /** True when the Run button may fire (the confirmation is still required). */
  readonly ok: boolean;
  /** How many companies the click would charge for. */
  readonly count: number;
  /** Blocking reason to render next to the button; null when ok. */
  readonly message: string | null;
}

/**
 * Validate a batch click BEFORE any money is spent.
 *
 *   - a volume above the backstop ceiling (50) is refused with "reduce it" — PM's
 *     resolution #5: the ceiling guards against a typo, not against a real decision;
 *   - an empty selection is refused (nothing to charge for);
 *   - a selection LARGER than the typed volume is refused: the user set the volume of
 *     this paid run, so the click must not silently exceed it.
 */
export function validateBatch(selectedCount: number, volume: number | null): BatchValidation {
  if (volume == null) {
    return { ok: false, count: selectedCount, message: 'Enter how many companies to check.' };
  }
  if (volume > AI_RUN_BATCH_CAP) {
    return {
      ok: false,
      count: selectedCount,
      message: `Too many at once — reduce the volume to ${AI_RUN_BATCH_CAP} or fewer.`,
    };
  }
  if (selectedCount <= 0) {
    return { ok: false, count: 0, message: 'Select at least one company.' };
  }
  if (selectedCount > volume) {
    return {
      ok: false,
      count: selectedCount,
      message: `${selectedCount} selected but the volume is ${volume} — deselect some or raise the volume.`,
    };
  }
  return { ok: true, count: selectedCount, message: null };
}

/**
 * E12-F17-T06 — the chain of ONE company on the verdict path in force (the admin toggle
 * «Вердикты AI-check: Jev / Haiku»). Unknown (quote still loading / failed) → a neutral line
 * that names no call count, so the copy is never wrong about the path.
 */
export function aiChainDescription(engine: 'jev' | 'haiku' | null): string {
  if (engine === 'jev') {
    return 'free site collection + 1 Jev verdict call + 1 Haiku firmographics call';
  }
  if (engine === 'haiku') {
    return 'free site collection + 4 Haiku calls: link selection, extraction, verification, grade explanation';
  }
  return 'free site collection + model calls';
}

/** E12-F17-T06 — the quote the confirmation prices the selection with. */
export interface AiCheckQuoteLike {
  verdictEngine: 'jev' | 'haiku';
  costPerCompanyUsd: number;
  priceIsFallbackEstimate: boolean;
}

/**
 * E12-F17-T06 — the price line of the confirmation: the estimate for `count` companies, the
 * per-company price and whether it rests on call history or the conservative constant.
 * `undefined` quote → still loading; `null` → could not be read (the run itself is not blocked).
 */
export function aiCheckPriceLine(
  count: number,
  quote: AiCheckQuoteLike | null | undefined,
  isError = false,
): string {
  if (isError || quote === null) return 'Price estimate unavailable.';
  if (quote === undefined) return 'Estimating the price…';
  const total = formatUsd(count * quote.costPerCompanyUsd);
  const per = formatUsd(quote.costPerCompanyUsd);
  const basis = quote.priceIsFallbackEstimate
    ? 'no call history yet, a conservative price is used'
    : 'average of recent calls';
  return `Estimated cost: ~${total} (${per} per company; ${basis}).`;
}

/**
 * Confirmation copy — MUST name the count (acceptance #2): every company is a
 * separate paid AI chain, so the user confirms exactly how many they are buying.
 * E12-F17-T06 — the chain text follows the verdict path in force (Jev / Haiku).
 */
export function confirmMessage(count: number, engine: 'jev' | 'haiku' | null = null): string {
  const noun = count === 1 ? 'company' : 'companies';
  return `Start the paid AI check for ${count} ${noun}? Each company is a separate paid AI chain (${aiChainDescription(engine)}). Nothing runs until you confirm.`;
}

// ---------------------------------------------------------------------------
// E06-F03-T14 — unconfirmed reference websites (warn, never block)
// ---------------------------------------------------------------------------

/** True when the row is a reference company whose code-found website nobody confirmed. */
export function hasUnconfirmedDomain(row: Pick<PipelineRow, 'websiteConfirmation'>): boolean {
  return row.websiteConfirmation === 'unconfirmed';
}

/** How many of the selected rows carry an unconfirmed reference website. */
export function countUnconfirmedDomains(
  rows: readonly Pick<PipelineRow, 'id' | 'websiteConfirmation'>[],
  selectedIds: readonly number[],
): number {
  const selected = new Set(selectedIds);
  return rows.filter((r) => selected.has(r.id) && hasUnconfirmedDomain(r)).length;
}

/** Batch-confirmation note naming how many selected websites are not confirmed; null for 0. */
export function unconfirmedDomainsNote(count: number): string | null {
  if (count <= 0) return null;
  const noun =
    count === 1 ? 'reference company has a website' : 'reference companies have websites';
  return `${count} ${noun} found automatically and not confirmed — the check may read another company. You can still run it, or confirm the websites in Review first.`;
}

// ---------------------------------------------------------------------------
// collected-sources panel formatting
// ---------------------------------------------------------------------------

/** Compact byte size for the sources panel ("12.4 KB"). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** Short ISO → local-ish display; empty string for a missing timestamp. */
export function formatCollectedAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().replace('T', ' ').slice(0, 16);
}

// ---------------------------------------------------------------------------
// E06-F05-T16 — manual check of a company the AI check could not read
// ---------------------------------------------------------------------------

/** Criterion labels + what to look for on the website (manual-check form). */
export const MANUAL_CHECK_FIELDS: ReadonlyArray<{
  readonly key: ManualCheckCriterion;
  readonly label: string;
  readonly hint: string;
}> = [
  { key: 'industry', label: 'Industry', hint: 'Is the company in the target industry?' },
  { key: 'subIndustry', label: 'Sub-industry', hint: 'Does it work in the target niche?' },
  { key: 'geo', label: 'Geo', hint: 'Does it have an office in the target location?' },
  { key: 'size', label: 'Company size', hint: 'Is the headcount inside the size window?' },
  { key: 'founded', label: 'Founded', hint: 'Was it founded inside the target years?' },
  { key: 'services', label: 'Services match', hint: 'Do its products/services match the brief?' },
];

/** Verdict choices of the form (a person never answers "unclear"). */
export const MANUAL_VERDICT_OPTIONS: ReadonlyArray<{
  readonly value: ManualCheckVerdict;
  readonly label: string;
}> = [
  { value: 'pass', label: 'Matches' },
  { value: 'partial', label: 'Partly' },
  { value: 'fail', label: 'Does not match' },
];

/** The form state: one verdict (or not yet chosen) + one note per criterion. */
export interface ManualCheckDraft {
  readonly verdicts: Readonly<Record<ManualCheckCriterion, ManualCheckVerdict | null>>;
  readonly notes: Readonly<Record<ManualCheckCriterion, string>>;
  readonly sourceUrl: string;
  readonly comment: string;
}

/**
 * The manual-check button shows on a FAILED row (the site blocked the AI check) and on
 * a row already checked by hand (to correct it) — never while a run is mid-flight.
 */
export function canCheckManually(row: Pick<PipelineRow, 'aiStatus' | 'manualCheck'>): boolean {
  if (isActiveAiRunStatus(row.aiStatus)) return false;
  return row.aiStatus === 'failed' || row.manualCheck != null;
}

function isVerdict(v: unknown): v is ManualCheckVerdict {
  return v === 'pass' || v === 'partial' || v === 'fail';
}

/**
 * Pre-fill the form from the row: a row already checked by hand re-opens with its own
 * verdicts and notes; any other row starts empty (an AI verdict from an earlier run is
 * not pre-selected — the person must look at the website themselves).
 */
export function manualCheckDraftFrom(
  row: Pick<PipelineRow, 'breakdown' | 'evidence' | 'manualCheck' | 'domain'>,
): ManualCheckDraft {
  const manual = row.manualCheck != null;
  const verdicts = {} as Record<ManualCheckCriterion, ManualCheckVerdict | null>;
  const notes = {} as Record<ManualCheckCriterion, string>;
  for (const key of MANUAL_CHECK_CRITERIA) {
    const v = row.breakdown?.[key];
    verdicts[key] = manual && isVerdict(v) ? v : null;
    const ev = row.evidence?.[key] as { quote?: unknown } | undefined;
    const quote = typeof ev?.quote === 'string' ? ev.quote : '';
    const note =
      manual && quote.startsWith(MANUAL_EVIDENCE_PREFIX)
        ? quote.slice(MANUAL_EVIDENCE_PREFIX.length)
        : '';
    notes[key] = note === MANUAL_NO_NOTE ? '' : note;
  }
  const sourceUrl = row.manualCheck?.url ?? (row.domain ? `https://${row.domain}` : '');
  return { verdicts, notes, sourceUrl, comment: '' };
}

/** Criteria still without a verdict (the save button stays disabled until none). */
export function missingVerdicts(draft: ManualCheckDraft): ManualCheckCriterion[] {
  return MANUAL_CHECK_CRITERIA.filter((k) => draft.verdicts[k] == null);
}

/** The request body for a complete draft; null while any verdict is missing. */
export function manualCheckBody(draft: ManualCheckDraft): ManualCheckInput | null {
  if (missingVerdicts(draft).length > 0) return null;
  const verdicts = draft.verdicts as Record<ManualCheckCriterion, ManualCheckVerdict>;
  const notes: Partial<Record<ManualCheckCriterion, string>> = {};
  for (const key of MANUAL_CHECK_CRITERIA) {
    const n = draft.notes[key].trim();
    if (n) notes[key] = n;
  }
  return {
    verdicts,
    notes,
    sourceUrl: draft.sourceUrl.trim(),
    comment: draft.comment.trim(),
  };
}

/** "Checked manually · 2026-10-14 10:00" — the mark next to manually entered data. */
export function manualCheckMarkText(mark: { at: string } | null | undefined): string {
  if (!mark) return '';
  const when = formatCollectedAt(mark.at);
  return when ? `Checked manually · ${when}` : 'Checked manually';
}
